import React from "react"

/**
 * Extracts a string representation from a cell value.
 * Handles primitives, React elements (via text/children props), and fallback.
 */
export const getCellString = (v: unknown): string => {
  if (typeof v === "string" || typeof v === "number") {
    return String(v)
  }
  if (React.isValidElement(v)) {
    const element = v as React.ReactElement<{
      text?: string | number
      children?: React.ReactNode
    }>
    const { text, children } = element.props ?? {}
    if (text != null) {
      return String(text)
    }
    if (children != null) {
      return Array.isArray(children)
        ? children.map(String).join(" ")
        : String(children)
    }
  }
  return ""
}

type SortableCol = {
  field?: string
  id?: string
  numeric?: boolean
  type?: string
  sortComparator?: (a: string, b: string) => number
}

/**
 * Compares two row values for sorting, using the column's sortComparator
 * if provided, numeric comparison when appropriate, or localeCompare.
 */
export const compareRowValues = (
  aVal: unknown,
  bVal: unknown,
  col?: SortableCol
): number => {
  if (col?.sortComparator) {
    return col.sortComparator(getCellString(aVal), getCellString(bVal))
  }
  if (col?.numeric || col?.type === "number") {
    return (Number(aVal) || 0) - (Number(bVal) || 0)
  }
  return getCellString(aVal).localeCompare(getCellString(bVal))
}

/**
 * Sorts rows by the given column and direction.
 * Returns a new array (does not mutate the input).
 */
export const sortRows = <T extends Record<string, unknown>>(
  rows: T[],
  sortColumn: string,
  sortDirection: "asc" | "desc",
  cols: SortableCol[]
): T[] => {
  if (!sortColumn) {
    return rows
  }

  const col = cols.find((c) => (c.field ?? c.id) === sortColumn)

  return [...rows].sort((a, b) => {
    const result = compareRowValues(a[sortColumn], b[sortColumn], col)
    return sortDirection === "desc" ? -result : result
  })
}

/**
 * Resolves the next sort state in a 3-state cycle:
 * none → asc → desc → none.
 *
 * When the DataGrid fires (desc → asc) on the same column,
 * we interpret it as "clear" and reset to unsorted.
 */
export const resolveNextSort = (
  incoming: { column: string; direction: "asc" | "desc" },
  currentColumn: string,
  currentDirection: "asc" | "desc"
): { column: string; direction: "asc" | "desc" } => {
  if (
    incoming.column === currentColumn &&
    incoming.direction === "asc" &&
    currentDirection === "desc"
  ) {
    return { column: "", direction: "asc" }
  }
  return { column: incoming.column, direction: incoming.direction }
}

/**
 * Parses the value from a SelectChangeEvent or ChangeEvent
 * for the rows-per-page selector. Returns the numeric value
 * or null when invalid.
 */
export const parseRowsPerPage = (
  raw: unknown
): number | null => {
  const value = Number(raw)
  return Number.isNaN(value) ? null : value
}
